import Phaser from "phaser";
import {
  getAvatarFrameForDirection,
  getAvatarSpriteConfig,
  normalizeAvatarName,
} from "./SpritesMap";

const STORY_COMPANION_DELAY_FRAMES = 12;
const STORY_COMPANION_DEPTH_OFFSET = 18;
const STORY_COMPANION_HOVER_DISTANCE = 2.4;
const STORY_COMPANION_HOVER_SPEED = 0.0075;
const STORY_COMPANION_Y_OFFSET = 10;

const readStoryCompanionRuntime = () =>
  typeof window !== "undefined" ? window.__storyCompanionRuntime || null : null;

const readStorySceneInputLock = () =>
  typeof window !== "undefined" ? Boolean(window.__storySceneInputLocked) : false;

export const createStoryCompanionFollower = (scene, player, options = {}) => ({
  player,
  sprite: null,
  history: [],
  activeCompanionId: "",
  hoverPhase: Math.random() * Math.PI * 2,
  scaleMultiplier: Number(options.scaleMultiplier || 1),
  depthOffset: Number(options.depthOffset || STORY_COMPANION_DEPTH_OFFSET),
});

export const destroyStoryCompanionFollower = (follower = null) => {
  if (!follower) return;
  follower.sprite?.destroy?.();
  follower.sprite = null;
  follower.history = [];
  follower.activeCompanionId = "";
};

const ensureCompanionSprite = (scene, follower, runtimeCompanion) => {
  if (!follower || !runtimeCompanion) return null;
  if (follower.sprite && follower.activeCompanionId === runtimeCompanion.id) {
    return follower.sprite;
  }

  destroyStoryCompanionFollower(follower);

  const avatarKey = normalizeAvatarName(runtimeCompanion.avatarKey || "io_default");
  const textureKey = `avatar-${avatarKey}`;
  const spriteConfig = getAvatarSpriteConfig(avatarKey);
  const sprite = scene.add
    .sprite(follower.player?.x || 0, follower.player?.y || 0, textureKey, 0)
    .setOrigin(0.5, 0.82)
    .setScale((spriteConfig.worldScale || 1) * follower.scaleMultiplier)
    .setAlpha(0.96);

  if (spriteConfig.trimBottom) {
    sprite.setCrop(
      0,
      0,
      spriteConfig.frameWidth,
      spriteConfig.frameHeight - spriteConfig.trimBottom,
    );
  }

  follower.sprite = sprite;
  follower.activeCompanionId = runtimeCompanion.id;
  return sprite;
};

// Story companions are local-only for now, so scenes read a lightweight
// runtime payload from `window` and reproduce the orb with delayed footsteps.
export const syncStoryCompanionFollower = (
  scene,
  follower,
  {
    vx = 0,
    vy = 0,
    direction = 0,
  } = {},
) => {
  if (!follower?.player) return;

  const runtimeCompanion = readStoryCompanionRuntime();
  if (!runtimeCompanion) {
    destroyStoryCompanionFollower(follower);
    return;
  }

  const sprite = ensureCompanionSprite(scene, follower, runtimeCompanion);
  if (!sprite) return;

  const movementLocked = readStorySceneInputLock();
  const isMoving = !movementLocked && (Math.abs(vx) > 0.05 || Math.abs(vy) > 0.05);

  follower.history.push({
    x: follower.player.x,
    y: follower.player.y,
    direction,
    isMoving,
  });

  if (follower.history.length > STORY_COMPANION_DELAY_FRAMES + 20) {
    follower.history.splice(0, follower.history.length - (STORY_COMPANION_DELAY_FRAMES + 20));
  }

  const delayedEntry =
    follower.history[Math.max(0, follower.history.length - STORY_COMPANION_DELAY_FRAMES - 1)] ||
    follower.history[0];

  if (!delayedEntry) return;

  const targetX = delayedEntry.x;
  const targetY = delayedEntry.y;
  const hoverOffset =
    Math.sin((scene.time?.now || 0) * STORY_COMPANION_HOVER_SPEED + follower.hoverPhase) *
    STORY_COMPANION_HOVER_DISTANCE;

  const nextX = Phaser.Math.Linear(sprite.x, targetX, 0.28);
  const nextY = Phaser.Math.Linear(
    sprite.y,
    targetY - STORY_COMPANION_Y_OFFSET,
    0.28,
  );
  const playerDepth = Number(follower.player?.depth);
  sprite.setDepth(
    Number.isFinite(playerDepth)
      ? playerDepth + 1
      : targetY + follower.depthOffset,
  );

  const frameIndex = delayedEntry.isMoving
    ? Math.floor(((scene.time?.now || 0) / 110) % 4)
    : 0;
  sprite.setFrame(
    getAvatarFrameForDirection(runtimeCompanion.avatarKey, delayedEntry.direction, frameIndex),
  );
  sprite.setPosition(nextX, nextY + hoverOffset);
};
